import React, { useMemo } from 'react';

/**
 * Neutral colors that should NOT get territory borders
 */
const NEUTRAL_COLORS = ['#9ca3af', 'rgb(156, 163, 175)', 'rgba(156, 163, 175, 1)', '#dfe6e9'];

function normalizeColor(color) {
    if (!color) return null;
    return color.toLowerCase().trim();
}

function isNeutralColor(color) {
    if (!color) return true;
    const normalized = normalizeColor(color);
    return NEUTRAL_COLORS.some(nc => normalizeColor(nc) === normalized);
}

/**
 * Find connected islands of cells using flood-fill (BFS)
 * Returns array of cell arrays, where each inner array is a connected group
 */
function findConnectedIslands(cells) {
    if (!cells || cells.length === 0) return [];

    const cellMap = new Map();
    cells.forEach(c => cellMap.set(`${c.x},${c.y}`, c));

    const visited = new Set();
    const islands = [];

    cells.forEach(cell => {
        const key = `${cell.x},${cell.y}`;
        if (visited.has(key)) return;

        // BFS to find all connected cells
        const island = [];
        const queue = [cell];
        visited.add(key);

        while (queue.length > 0) {
            const current = queue.shift();
            island.push(current);

            // Check 4 neighbors (up, down, left, right)
            const neighbors = [
                { x: current.x, y: current.y - 1 },
                { x: current.x, y: current.y + 1 },
                { x: current.x - 1, y: current.y },
                { x: current.x + 1, y: current.y },
            ];

            for (const n of neighbors) {
                const nKey = `${n.x},${n.y}`;
                if (visited.has(nKey)) continue;
                if (!cellMap.has(nKey)) continue;

                visited.add(nKey);
                queue.push(cellMap.get(nKey));
            }
        }

        islands.push(island);
    });

    return islands;
}

/**
 * Calculate SVG paths for a single island (group of connected cells)
 * Returns array of path strings - one for outer boundary, plus one for each hole
 */
function calculateIslandPaths(islandCells, cellSize, gap) {
    if (!islandCells || islandCells.length === 0) return [];

    const cellWithGap = cellSize + gap;
    const cellSet = new Set(islandCells.map(c => `${c.x},${c.y}`));

    // Work in grid units (each cell is 1x1)
    // Collect external edges in grid coordinates
    // Each edge is stored with direction for proper contour tracing
    const gridEdges = [];

    islandCells.forEach(cell => {
        const { x, y } = cell;

        // Top edge (left to right)
        if (!cellSet.has(`${x},${y - 1}`)) {
            gridEdges.push({ x1: x, y1: y, x2: x + 1, y2: y });
        }
        // Right edge (top to bottom)
        if (!cellSet.has(`${x + 1},${y}`)) {
            gridEdges.push({ x1: x + 1, y1: y, x2: x + 1, y2: y + 1 });
        }
        // Bottom edge (right to left)
        if (!cellSet.has(`${x},${y + 1}`)) {
            gridEdges.push({ x1: x + 1, y1: y + 1, x2: x, y2: y + 1 });
        }
        // Left edge (bottom to top)
        if (!cellSet.has(`${x - 1},${y}`)) {
            gridEdges.push({ x1: x, y1: y + 1, x2: x, y2: y });
        }
    });

    if (gridEdges.length === 0) return [];

    // Build adjacency map: for each vertex, store outgoing edges
    // Key: "x,y", Value: array of {toX, toY, edgeIndex}
    const adjacency = new Map();
    gridEdges.forEach((e, idx) => {
        const k1 = `${e.x1},${e.y1}`;
        if (!adjacency.has(k1)) adjacency.set(k1, []);
        adjacency.get(k1).push({ toX: e.x2, toY: e.y2, edgeIndex: idx });
    });

    // Track used edges
    const usedEdges = new Set();
    const contours = [];

    // Trace all contours (outer boundary + holes)
    while (usedEdges.size < gridEdges.length) {
        // Find an unused edge to start from
        let startEdgeIdx = -1;
        for (let i = 0; i < gridEdges.length; i++) {
            if (!usedEdges.has(i)) {
                startEdgeIdx = i;
                break;
            }
        }
        if (startEdgeIdx === -1) break;

        // Trace this contour
        const contour = traceContour(gridEdges, adjacency, usedEdges, startEdgeIdx);
        if (contour && contour.length >= 3) {
            contours.push(contour);
        }
    }

    // Convert each contour to a path
    // Skip small contours - single-cell holes (4 corners) shouldn't have borders drawn
    const paths = [];
    for (const contour of contours) {
        const simplified = simplifyContour(contour);
        if (simplified.length < 3) continue;

        // Calculate bounding box to detect single-cell regions
        const minX = Math.min(...simplified.map(p => p.x));
        const maxX = Math.max(...simplified.map(p => p.x));
        const minY = Math.min(...simplified.map(p => p.y));
        const maxY = Math.max(...simplified.map(p => p.y));

        // Skip if bounding box is 1x1 (single cell) - these are hole borders we don't want
        if (maxX - minX <= 1 && maxY - minY <= 1) continue;

        const pixelPoints = simplified.map(p => ({
            x: p.x * cellWithGap - gap / 2,
            y: p.y * cellWithGap - gap / 2
        }));

        const pathD = createSmoothPath(pixelPoints, 8);
        if (pathD) {
            paths.push(pathD);
        }
    }

    return paths;
}

/**
 * Trace a single contour starting from a given edge
 */
function traceContour(gridEdges, adjacency, usedEdges, startEdgeIdx) {
    const startEdge = gridEdges[startEdgeIdx];
    const rawPoints = [{ x: startEdge.x1, y: startEdge.y1 }];
    usedEdges.add(startEdgeIdx);

    let current = { x: startEdge.x2, y: startEdge.y2 };
    const startKey = `${startEdge.x1},${startEdge.y1}`;
    const maxIterations = gridEdges.length * 2;
    let iterations = 0;

    while (iterations < maxIterations) {
        iterations++;
        const currentKey = `${current.x},${current.y}`;

        // Check if we've completed the loop
        if (currentKey === startKey && rawPoints.length >= 3) {
            break;
        }

        rawPoints.push({ x: current.x, y: current.y });

        // Find next unused edge from current vertex
        const outgoing = adjacency.get(currentKey) || [];
        let nextEdge = null;

        for (const edge of outgoing) {
            if (!usedEdges.has(edge.edgeIndex)) {
                nextEdge = edge;
                break;
            }
        }

        if (!nextEdge) break;

        usedEdges.add(nextEdge.edgeIndex);
        current = { x: nextEdge.toX, y: nextEdge.toY };
    }

    return rawPoints;
}

/**
 * Calculate SVG paths for all territory contours
 */
function calculateTerritoryPaths(cells, cellSize, gap) {
    if (!cells || cells.length === 0) return [];

    // Group cells by color
    const colorGroups = new Map();
    cells.forEach(cell => {
        if (!cell.chipColor || isNeutralColor(cell.chipColor)) return;
        const color = normalizeColor(cell.chipColor);
        if (!colorGroups.has(color)) {
            colorGroups.set(color, []);
        }
        colorGroups.get(color).push(cell);
    });

    const paths = [];

    colorGroups.forEach((groupCells, color) => {
        // Find connected islands within this color group
        const islands = findConnectedIslands(groupCells);

        // Draw borders for each island (including holes)
        // Skip single-cell islands - they don't need territory borders
        islands.forEach(island => {
            if (island.length < 2) return;

            const islandPaths = calculateIslandPaths(island, cellSize, gap);
            islandPaths.forEach(pathD => {
                paths.push({ color: island[0].chipColor, path: pathD });
            });
        });
    });

    return paths;
}

/**
 * Remove points that lie on a straight line between their neighbors
 */
function simplifyContour(points) {
    if (points.length < 3) return points;

    const result = [];
    const n = points.length;

    for (let i = 0; i < n; i++) {
        const prev = points[(i - 1 + n) % n];
        const curr = points[i];
        const next = points[(i + 1) % n];

        // Check if curr is on the line between prev and next
        // Using cross product to check collinearity
        const cross = (curr.x - prev.x) * (next.y - prev.y) - (curr.y - prev.y) * (next.x - prev.x);

        // If not collinear, keep this point (it's a corner)
        if (Math.abs(cross) > 0.001) {
            result.push(curr);
        }
    }

    return result;
}

/**
 * Create SVG path with rounded corners
 */
function createSmoothPath(points, radius) {
    if (points.length < 3) return '';

    const n = points.length;
    let path = '';

    for (let i = 0; i < n; i++) {
        const prev = points[(i - 1 + n) % n];
        const curr = points[i];
        const next = points[(i + 1) % n];

        const v1 = { x: curr.x - prev.x, y: curr.y - prev.y };
        const v2 = { x: next.x - curr.x, y: next.y - curr.y };

        const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
        const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

        if (len1 === 0 || len2 === 0) continue;

        const r = Math.min(radius, len1 / 2, len2 / 2);

        const p1 = { x: curr.x - (v1.x / len1) * r, y: curr.y - (v1.y / len1) * r };
        const p2 = { x: curr.x + (v2.x / len2) * r, y: curr.y + (v2.y / len2) * r };

        if (i === 0) {
            path = `M ${p1.x} ${p1.y}`;
        } else {
            path += ` L ${p1.x} ${p1.y}`;
        }

        path += ` Q ${curr.x} ${curr.y} ${p2.x} ${p2.y}`;
    }

    path += ' Z';
    return path;
}

/**
 * SVG overlay component for territory borders
 */
function TerritoryOverlay({ cells, mapSize, cellSize = 60, gap = 1, paddingTop = 0, paddingLeft = 0 }) {
    const paths = useMemo(() => {
        return calculateTerritoryPaths(cells, cellSize, gap);
    }, [cells, cellSize, gap]);

    if (paths.length === 0) return null;

    const totalSize = mapSize * (cellSize + gap);

    return (
        <svg
            style={{
                position: 'absolute',
                top: paddingTop,
                left: paddingLeft,
                width: totalSize,
                height: totalSize,
                pointerEvents: 'none',
                zIndex: 10,
                overflow: 'visible',
            }}
            viewBox={`0 0 ${totalSize} ${totalSize}`}
        >
            {paths.map((p, idx) => (
                <path
                    key={idx}
                    d={p.path}
                    fill="none"
                    stroke={p.color}
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ))}
        </svg>
    );
}

export default TerritoryOverlay;
